import React, { useCallback, useMemo } from "react";
import { ToastContainer } from "react-toastify";
import {
    Calendar,
    User,
    MapPin,
    DollarSign,
    PlusCircle,
    Trash2,
    CheckCircle,
    XCircle,
    Clock,
    Search,
    ChevronRight,
    ShieldAlert,
    FileText,
    Plus
} from "lucide-react";

const STATUS_CONFIG = {
    pending: { color: "bg-amber-100 text-amber-700", icon: <Clock size={14} />, label: "Pending" },
    approve: { color: "bg-emerald-100 text-emerald-700", icon: <CheckCircle size={14} />, label: "Approved" },
    reject: { color: "bg-rose-100 text-rose-700", icon: <XCircle size={14} />, label: "Rejected" },
};

const InputField = ({ label, value, onChange, type = "text", placeholder, icon, error }) => (
    <div className="w-full">
        <label className="text-[11px] font-semibold text-gray-600 flex items-center gap-1 mb-1">
            {icon} {label}
        </label>

        <input
            type={type}
            value={value || ""}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-200 outline-none
        ${error ? "border-red-400 bg-red-50" : "border-gray-300"}
      `}
        />

        {error && <p className="text-red-500 text-[10px] mt-1">{error}</p>}
    </div>
);

const VisaCard = ({ visa, index, onRemove, onChange, errors }) => (
    <div className="bg-white border rounded-xl p-4 sm:p-5 md:p-6 shadow-sm">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-sm text-blue-700">
                Entry #{index + 1}
            </h3>

            {index > 0 && (
                <button onClick={() => onRemove(index)} className="text-red-500">
                    <Trash2 size={18} />
                </button>
            )}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">

            <InputField
                type="date"
                label="Applied Date"
                icon={<Calendar size={14} />}
                value={visa.appliedDate}
                onChange={(v) => onChange(index, "appliedDate", v)}
                error={errors[`appliedDate_${index}`]}
            />

            <InputField
                label="Applied Through"
                icon={<ChevronRight size={14} />}
                value={visa.appliedThrough}
                onChange={(v) => onChange(index, "appliedThrough", v)}
            />

            <InputField
                label="Agent Name"
                icon={<User size={14} />}
                value={visa.agentName}
                onChange={(v) => onChange(index, "agentName", v)}
            />

            <InputField
                type="number"
                label="Fees Paid"
                icon={<DollarSign size={14} />}
                value={visa.feesPaid}
                onChange={(v) => onChange(index, "feesPaid", v)}
            />

            <InputField
                type="date"
                label="Biometric Date"
                icon={<Calendar size={14} />}
                value={visa.biometricDate}
                onChange={(v) => onChange(index, "biometricDate", v)}
            />

            <InputField
                label="Biometric City"
                icon={<MapPin size={14} />}
                value={visa.biometricCity}
                onChange={(v) => onChange(index, "biometricCity", v)}
            />

            <InputField
                type="date"
                label="Issue Date"
                icon={<CheckCircle size={14} />}
                value={visa.issueDate}
                onChange={(v) => onChange(index, "issueDate", v)}
            />

            <InputField
                type="date"
                label="Expiry Date"
                icon={<XCircle size={14} />}
                value={visa.expiryDate}
                onChange={(v) => onChange(index, "expiryDate", v)}
            />

            <InputField
                label="Referral"
                icon={<Search size={14} />}
                value={visa.howYouFoundAgent}
                onChange={(v) => onChange(index, "howYouFoundAgent", v)}
            />

        </div>
    </div>
);

const CanadaForm = ({
    visas = [],
    setVisas,
    errors = {},
    handleChange,
    getEmptyVisa
}) => {

    const addEntry = useCallback(() => {
        setVisas(prev => [...prev, getEmptyVisa()]);
    }, [setVisas, getEmptyVisa]);

    const removeEntry = useCallback((i) => {
        setVisas(prev => prev.filter((_, index) => index !== i));
    }, [setVisas]);

    // const changeField = useCallback((i, field, value) => {
    //     handleChange?.(i, field, value);
    // }, [handleChange]);

    const changeField = useCallback((i, field, value) => {
        setVisas(prev => {
            const updated = [...prev];
            updated[i] = { ...updated[i], [field]: value };
            return updated;
        });
    }, [setVisas]);

    return (
        <div className="w-full px-3 sm:px-4 md:px-6 py-4">

            <ToastContainer />

            <div className="max-w-7xl mx-auto">

                {/* Header */}
                <div className="bg-blue-900 text-white rounded-xl p-4 md:p-6 mb-6">
                    <h1 className="text-lg md:text-xl font-bold">Canada Visa History</h1>
                    <p className="text-xs opacity-70">Fill your visa details</p>
                </div>

                {/* Cards */}
                <div className="space-y-4">
                    {visas.map((visa, i) => (
                        <VisaCard
                            key={i}
                            visa={visa}
                            index={i}
                            onRemove={removeEntry}
                            onChange={changeField}
                            errors={errors}
                        />
                    ))}
                </div>

                {/* Add Button */}
                <div className="mt-6 flex flex-col sm:flex-row gap-3 items-center justify-between">
                    <button
                        onClick={addEntry}
                        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg"
                    >
                        <Plus size={16} /> Add Entry
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                        * Required fields
                    </p>
                </div>

            </div>
        </div>
    );
};

export default CanadaForm;